import { useCheckoutCartState } from '@monofront/business/contexts';
import { useCheckoutPayment } from '../contexts';
import { CREATE_PAYMENT_INTENT_FOR_NEW_PAYPAL_AGREEMENT } from '@monofront/business/queries';
import { useMutation } from '@apollo/client';
import {
  ICreatePaymentForNewPaypalAgreementIntentQueryResult,
  PaymentMethodSource,
} from '@monofront/types';
import useCompleteOrder from '../hooks/useCompleteOrder';
import PaypalAgreementButton from '@components/PaymentForms/PaypalAgreementButton';
import useAddPaymentMethod from '@features/PaymentMethods/useAddPaymentMethod';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';

/**
 * Display a Pay CTA to add a new Paypal Agreement and Pay with it (only for MAAP flow)
 */
const PayWithNewPaypalAgreementCta: React.FC = () => {
  const { checkoutCart } = useCheckoutCartState();
  const { setIsOrderValidationInProgress, selectedNewPaypal } =
    useCheckoutPayment();
  const { completeOrder } = useCompleteOrder();
  const { handleAddPaymentMethod } = useAddPaymentMethod(false, 'checkout');
  const { handlePaymentError } = useHandlePaymentError();

  const [createPaymentIntentForNewPaypalAgreement] =
    useMutation<ICreatePaymentForNewPaypalAgreementIntentQueryResult>(
      CREATE_PAYMENT_INTENT_FOR_NEW_PAYPAL_AGREEMENT,
    );

  if (
    !checkoutCart?.id ||
    !checkoutCart?.payment?.id ||
    !selectedNewPaypal?.paymentMethodCode
  )
    return null;

  return (
    <PaypalAgreementButton
      onStartBillingAgreement={() => {
        setIsOrderValidationInProgress(true);
      }}
      onCancel={() => {
        setIsOrderValidationInProgress(false);
      }}
      onError={(error: any) => {
        setIsOrderValidationInProgress(false);

        const isApolloError =
          typeof error?.stack === 'string' &&
          error.stack.includes('ApolloError');

        handlePaymentError({
          code: 'AUTHORIZE_NEW_PAYPAL_BILLING_AGREEMENT_FAILED',
          message:
            !isApolloError && typeof error?.message === 'string'
              ? error.message
              : 'Cannot generate or authorize a Paypal Billing Agreement (MAAP flow)',
          shouldDisplayError: true,
          error,
          context: {
            paypal_method_type: selectedNewPaypal?.paypalType,
          },
        });
      }}
      onApprove={async (paymentMethodId) => {
        try {
          const { data } = await createPaymentIntentForNewPaypalAgreement({
            variables: {
              tokenValue: checkoutCart.id,
              cartPaymentId: checkoutCart.payment?.id,
              paypalPaymentToken: paymentMethodId,
              paymentMethodCode: selectedNewPaypal.paymentMethodCode,
            },
          });

          if (!data?.createPaymentIntentForNewPaypalAgreement?.payment) {
            throw new Error(
              'Create Payment Intent failed with empty response for Paypal Agreement (MAAP flow).',
            );
          }
        } catch (error: any) {
          setIsOrderValidationInProgress(false);
          handlePaymentError({
            code: 'PAY_WITH_NEW_PAYPAL_AGREEMENT_FAILED',
            message:
              !error?.graphQLErrors && error?.message
                ? error.message
                : 'Cannot pay with an authorize new Paypal Agreement (MAAP flow).',
            shouldDisplayError: true,
            error,
            context: {
              paypal_method_id: paymentMethodId,
              paypal_method_type: selectedNewPaypal?.paypalType,
            },
          });

          return;
        }

        try {
          await handleAddPaymentMethod(
            paymentMethodId,
            PaymentMethodSource.PAYPAL,
          );
        } catch (error: any) {
          handlePaymentError({
            code: 'ATTACH_WITH_PAYPAL_AGREEMENT_FAILED',
            message:
              !error?.graphQLErrors && error?.message
                ? error.message
                : 'Cannot attach a new paypal agreement (MAAP flow).',
            error,
            context: {
              paypal_method_id: paymentMethodId,
              paypal_method_type: selectedNewPaypal?.paypalType,
            },
          });
        }

        await completeOrder();
      }}
    />
  );
};

export default PayWithNewPaypalAgreementCta;
